// 2D geometry helpers — pure functions, no DOM, no globals.
//
// closestPointOnSegment returns { dist, t, point: {x, y} } — note the
// shape unifies the prototype's `{x, y, dist}` and the editor's
// `{dist, t, point}` from before extraction. The richer shape is
// better: `t` is needed for snap-to-edge, `point` keeps results
// composable with other geometry helpers.
//
// For hot paths that run thousands of times per tick (AI controller's
// racing-line lookups, surfaceAt, applyWallCollision), the
// "Into" variants below take an out-param object and avoid all
// allocation. They return the same object for chaining; callers
// MUST treat the out-param as scratch (don't keep references after
// the next call). The per-call object/array allocation in the
// allocating variants was the source of the ~2-3 sec GC stutter
// at high car counts.

// Closest point on segment a→b to point p.
// Returns { dist, t, point: {x, y} } where t is the segment parameter
// in [0, 1].
export function closestPointOnSegment(p, a, b) {
  const dx = b.x - a.x, dy = b.y - a.y;
  const lenSq = dx * dx + dy * dy;
  if (lenSq < 1e-12) {
    return { dist: Math.hypot(p.x - a.x, p.y - a.y), t: 0, point: { x: a.x, y: a.y } };
  }
  let t = ((p.x - a.x) * dx + (p.y - a.y) * dy) / lenSq;
  t = Math.max(0, Math.min(1, t));
  const px = a.x + t * dx;
  const py = a.y + t * dy;
  return { dist: Math.hypot(p.x - px, p.y - py), t, point: { x: px, y: py } };
}

// No-alloc variant. `out` must have `point: {x, y}` already so we can
// mutate in place. Returns `out`.
export function closestPointOnSegmentInto(p, a, b, out) {
  const dx = b.x - a.x, dy = b.y - a.y;
  const lenSq = dx * dx + dy * dy;
  if (lenSq < 1e-12) {
    out.dist = Math.hypot(p.x - a.x, p.y - a.y);
    out.t = 0;
    out.point.x = a.x;
    out.point.y = a.y;
    return out;
  }
  let t = ((p.x - a.x) * dx + (p.y - a.y) * dy) / lenSq;
  if (t < 0) t = 0;
  else if (t > 1) t = 1;
  const px = a.x + t * dx;
  const py = a.y + t * dy;
  out.dist = Math.hypot(p.x - px, p.y - py);
  out.t = t;
  out.point.x = px;
  out.point.y = py;
  return out;
}

// Factory for the scratch object the "Into" variants accept. Callers
// should hold one of these per long-lived owner (per car, per query
// site) and reuse it.
export function makeNearestResult() {
  return { dist: 0, t: 0, point: { x: 0, y: 0 }, segmentIdx: -1 };
}

// Walks every segment of a dense polyline and returns the closest hit.
// `closed` controls whether to include the wrap-around segment
// (last → first) — true for centerlines and closed walls/patches,
// false for open paths like pit lanes.
//
// Returns null if the polyline has fewer than 2 points.
// Returns the closestPointOnSegment shape with an extra `segmentIdx`
// for callers that need to know which segment hit.
export function nearestPointOnDense(p, dense, closed) {
  const n = dense.length;
  if (n < 2) return null;
  let best = null;
  const segCount = closed ? n : n - 1;
  for (let i = 0; i < segCount; i++) {
    const r = closestPointOnSegment(p, dense[i], dense[(i + 1) % n]);
    if (!best || r.dist < best.dist) {
      best = { ...r, segmentIdx: i };
    }
  }
  return best;
}

// No-alloc variant. `out` must be a `makeNearestResult()` object;
// returns it (mutated) or null on too-short input. Uses a single
// inline scratch slot so we don't allocate an inner result per
// segment either.
const _scratchSeg = { dist: 0, t: 0, point: { x: 0, y: 0 } };
export function nearestPointOnDenseInto(p, dense, closed, out) {
  const n = dense.length;
  if (n < 2) return null;
  out.dist = Infinity;
  out.segmentIdx = -1;
  const segCount = closed ? n : n - 1;
  for (let i = 0; i < segCount; i++) {
    closestPointOnSegmentInto(p, dense[i], dense[(i + 1) % n], _scratchSeg);
    if (_scratchSeg.dist < out.dist) {
      out.dist = _scratchSeg.dist;
      out.t = _scratchSeg.t;
      out.point.x = _scratchSeg.point.x;
      out.point.y = _scratchSeg.point.y;
      out.segmentIdx = i;
    }
  }
  return out;
}

// Like nearestPointOnDenseInto but only scans segments [startIdx-window,
// startIdx+window] (wrapping for closed polylines). For per-tick
// queries where the previous result is known and the point can't have
// moved far. Falls back to a full scan if the windowed search returns
// a result at the edge of the window — that's a hint the cached
// position has drifted out of range (spinout, teleport, reset).
//
// Returns out, or null on too-short input.
export function nearestPointOnDenseWindowedInto(p, dense, closed, startIdx, window, out) {
  const n = dense.length;
  if (n < 2) return null;
  const segCount = closed ? n : n - 1;
  if (startIdx < 0 || startIdx >= segCount) {
    return nearestPointOnDenseInto(p, dense, closed, out);
  }
  out.dist = Infinity;
  out.segmentIdx = -1;
  let bestRel = -1;
  for (let k = -window; k <= window; k++) {
    let i = startIdx + k;
    if (closed) {
      i = ((i % segCount) + segCount) % segCount;
    } else {
      if (i < 0 || i >= segCount) continue;
    }
    closestPointOnSegmentInto(p, dense[i], dense[(i + 1) % n], _scratchSeg);
    if (_scratchSeg.dist < out.dist) {
      out.dist = _scratchSeg.dist;
      out.t = _scratchSeg.t;
      out.point.x = _scratchSeg.point.x;
      out.point.y = _scratchSeg.point.y;
      out.segmentIdx = i;
      bestRel = k;
    }
  }
  // Hit the edge of the window — cached position has drifted. Re-scan
  // the full polyline so the next call gets a fresh anchor.
  if (bestRel === -window || bestRel === window) {
    return nearestPointOnDenseInto(p, dense, closed, out);
  }
  return out;
}

// Standard ray-casting point-in-polygon. At 14-subdivision densification
// the polygon matches a smooth Catmull-Rom patch to well under a meter —
// close enough that no car notices the discretization.
export function pointInPolygon(p, polygon) {
  let inside = false;
  for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i++) {
    const xi = polygon[i].x, yi = polygon[i].y;
    const xj = polygon[j].x, yj = polygon[j].y;
    if (((yi > p.y) !== (yj > p.y)) &&
        (p.x < (xj - xi) * (p.y - yi) / (yj - yi) + xi)) {
      inside = !inside;
    }
  }
  return inside;
}

// 2D line segment intersection. Returns { x, y, t } if p1→p2 and p3→p4
// cross within both parameter ranges, where t is the parameter along
// p1→p2. Returns null on parallel / non-overlapping. Used by timing-line
// crossing detection and wall raycast geometry.
export function segmentIntersect(p1, p2, p3, p4) {
  const x1 = p1.x, y1 = p1.y, x2 = p2.x, y2 = p2.y;
  const x3 = p3.x, y3 = p3.y, x4 = p4.x, y4 = p4.y;
  const denom = (x1 - x2) * (y3 - y4) - (y1 - y2) * (x3 - x4);
  if (Math.abs(denom) < 1e-9) return null;
  const t = ((x1 - x3) * (y3 - y4) - (y1 - y3) * (x3 - x4)) / denom;
  const u = -((x1 - x2) * (y1 - y3) - (y1 - y2) * (x1 - x3)) / denom;
  if (t < 0 || t > 1 || u < 0 || u > 1) return null;
  return { x: x1 + t * (x2 - x1), y: y1 + t * (y2 - y1), t };
}

export function clamp(x, a, b) {
  return Math.max(a, Math.min(b, x));
}
